/**
 * Turnstile, client side.
 *
 * The one piece of third-party script in the app, and it is loaded **only when
 * a room is actually being created** — not on boot, not on the home screen.
 * That keeps the promise made in the privacy sheet ("no third-party trackers")
 * true for the overwhelming majority of sessions, since most people never
 * create a 1v1 room.
 *
 * Inert without `VITE_TURNSTILE_SITE_KEY`: `getToken` resolves to undefined and
 * the server, also unconfigured, lets the request through. Configure both ends
 * or neither — a site key with no server secret protects nothing, and a secret
 * with no site key blocks every room.
 */

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi | null> | null = null;

function load(): Promise<TurnstileApi | null> {
  if (!siteKey) return Promise.resolve(null);
  if (loading) return loading;

  loading = new Promise<TurnstileApi | null>((resolve) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    // A blocked script must not block the game. The server treats a missing
    // token as a failure only when it is itself configured, so the worst case
    // is a clear "challenge failed" rather than a silent hang.
    script.onerror = () => resolve(null);
    script.onload = () => resolve(window.turnstile ?? null);
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Runs the challenge and resolves with a token.
 *
 * Resolves `undefined` when Turnstile is not configured or could not load —
 * never rejects, because a challenge failing to appear should not be the thing
 * that stops someone playing.
 */
export async function getToken(timeoutMs = 8000): Promise<string | undefined> {
  const api = await load();
  if (!api) return undefined;

  return new Promise<string | undefined>((resolve) => {
    // Rendered into a detached, hidden element: the invisible widget needs a
    // node to attach to but has nothing to show unless it decides to challenge,
    // in which case it manages its own overlay.
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;bottom:0;left:0;width:0;height:0;overflow:hidden';
    document.body.appendChild(host);

    let settled = false;
    const finish = (token?: string): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (id) api.remove(id);
      } catch {
        /* already gone */
      }
      host.remove();
      resolve(token);
    };

    // A challenge that never resolves must not leave the button spinning
    // forever. Giving up unchallenged is the right failure: the cost this
    // protects against is a script in a loop, not one slow human.
    const timer = setTimeout(() => finish(undefined), timeoutMs);

    let id: string | undefined;
    try {
      id = api.render(host, {
        sitekey: siteKey,
        // Invisible unless the visitor looks automated -- the plan's
        // "invisible to normal users".
        appearance: 'interaction-only',
        callback: (token: string) => finish(token),
        'error-callback': () => finish(undefined),
        'timeout-callback': () => finish(undefined),
      });
    } catch {
      finish(undefined);
    }
  });
}
